import CityAvatar from "@/components/Cards/CityAvatar";
import classNames from "classnames";
import Link from "next/link";
import { useState } from "react";
import {
  MdAltRoute,
  MdLocationCity,
  MdLocationPin,
  RxArrowRight,
} from "../../Atoms/AppIcons";
import { keywordMap } from "../../../core/enums";
import {
  GetCitiesAndPrefecturesWithProductsQuery,
  Location,
} from "../../../graphql/operations";
import { getCityUrl } from "../../../utils/getCityUrl";
import Loader from "../../Atoms/Loader";
import OptimizedLink from "../../Atoms/OptimizedLink";
import { Text } from "../../Atoms/Typography";
import styles from "./Header.module.scss";

export type DestinationPickerLocation =
  GetCitiesAndPrefecturesWithProductsQuery["locations"]["records"][number];

type DestinationTab = "cities" | "itineraries";

type DestinationPickerProps = {
  locations: DestinationPickerLocation[];
  onCityPick?: () => void;
  selectedId?: string;
  includeAnywhere?: boolean;
  countryId?: string;
  countryName?: string;
  countrySlug?: string;
  loading?: boolean;
};

export default function DestinationPicker({
  onCityPick,
  selectedId,
  locations,
  countryId,
  countryName = "",
  countrySlug,
  loading,
}: DestinationPickerProps) {
  const [activeTab, setActiveTab] = useState<DestinationTab>("cities");

  if (loading) {
    return (
      <div className={`${styles.SubMenu} ${styles.MegaMenu}`}>
        <div style={{ width: 250, padding: "1rem" }}>
          <Loader />
        </div>
      </div>
    );
  }

  const cities = (
    countryId
      ? locations.filter((c) => c.country.id === countryId)
      : locations || []
  ).filter(Boolean);

  const midpoint = 5;
  const popularCities = cities.slice(0, midpoint);
  const moreCities = cities.slice(midpoint);

  const slug = countrySlug ?? countryName.toLowerCase();

  return (
    <div className={`${styles.SubMenu} ${styles.MegaMenu}`}>
      <div className={styles.MegaMenuSidebar}>
        <button
          type="button"
          className={classNames(styles.SidebarItem, {
            [styles.SidebarItemActive]: activeTab === "cities",
          })}
          onClick={() => setActiveTab("cities")}
        >
          <MdLocationPin size={22} />
          <span>
            <b>Cities</b>
            <Text size="smaller" muted>
              Explore tours by city
            </Text>
          </span>
        </button>
        <button
          type="button"
          className={classNames(styles.SidebarItem, {
            [styles.SidebarItemActive]: activeTab === "itineraries",
          })}
          onClick={() => setActiveTab("itineraries")}
        >
          <MdAltRoute size={22} />
          <span>
            <b>Itineraries</b>
            <Text size="smaller" muted>
              Find tours by theme or experience
            </Text>
          </span>
        </button>

        {slug && (
          <Link href={`/tours-${slug}?ref=nav`} className={styles.ViewAllLink}>
            View all {countryName} tours
            <RxArrowRight size={16} />
          </Link>
        )}
      </div>

      <div className={styles.MegaMenuContent}>
        {activeTab === "cities" ? (
          <>
            <CityColumn
              title="Popular Cities"
              cities={popularCities}
              onCityPick={onCityPick}
              selectedId={selectedId}
            />
            {moreCities.length > 0 && (
              <CityColumn
                title="More Cities"
                cities={moreCities}
                onCityPick={onCityPick}
                selectedId={selectedId}
              />
            )}
          </>
        ) : (
          <div className={styles.MegaMenuColumn}>
            <p className={styles.MegaMenuColumnTitle}>Itineraries</p>
            <ul className={styles.MegaMenuItems}>
              {cities.map((location) => (
                <li key={location.id}>
                  <OptimizedLink
                    href={`/multiday-itineraries/${location.slug}`}
                    prefetchMode="disable"
                    className={styles.DestinationPickerItem}
                    onClick={onCityPick}
                  >
                    <CityAvatar
                      label={location.translation.name}
                      navImageUrl={location.navImageUrl}
                      icon={<MdAltRoute size={18} />}
                    />
                    <span>
                      <b>{location.translation.name} Itineraries</b>
                      <Text size="smaller" muted>
                        Explore popular themes and experiences in{" "}
                        {location.translation.name}
                      </Text>
                    </span>
                  </OptimizedLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

type CityColumnProps = {
  title: string;
  cities: DestinationPickerLocation[];
} & Pick<DestinationPickerProps, "onCityPick" | "selectedId">;

function CityColumn({
  title,
  cities,
  onCityPick,
  selectedId,
}: CityColumnProps) {
  return (
    <div className={styles.MegaMenuColumn}>
      <p className={styles.MegaMenuColumnTitle}>{title}</p>
      <ul className={styles.MegaMenuItems}>
        {cities.map(
          (location) =>
            location && (
              <DestinationListItem
                navImageUrl={location.navImageUrl}
                key={location.id}
                destinationNames={location.destinationNames}
                id={location.id}
                url={`/tours-${location.country.name.toLowerCase()}/${getCityUrl(
                  location,
                )}`}
                name={location.translation.name}
                onCityPick={onCityPick}
                selectedId={selectedId}
              />
            ),
        )}
      </ul>
    </div>
  );
}

type DestinationListItemsProps = {
  id: Location["id"] | undefined;
  destinationNames: Location["destinationNames"];
  navImageUrl?: string | null;
  url: string;
  name: Location["translation"]["name"];
} & Pick<DestinationPickerProps, "onCityPick" | "selectedId">;

function DestinationListItem({
  id,
  name,
  onCityPick,
  url,
  selectedId,
  destinationNames,
  navImageUrl,
}: DestinationListItemsProps) {
  return (
    <li key={id}>
      <OptimizedLink
        href={url}
        prefetchMode="disable"
        className={classNames(styles.DestinationPickerItem, {
          [styles.Selected]: id === selectedId,
        })}
        onClick={onCityPick}
      >
        <CityAvatar
          label={name}
          navImageUrl={navImageUrl}
          icon={<MdLocationCity size={18} />}
        />
        <span>
          <b>{name}</b>
          <Text size="smaller" muted>
            {destinationNames ?? keywordMap[id]}
          </Text>
        </span>
      </OptimizedLink>
    </li>
  );
}
